import type { RechenmauerExerciseConfig, RechenmauerRunSettings } from "@calega/content";
import { randomGenerator } from "../run-random";
import { generateUniqueRunItems, pickRunItems, type RunSelection } from "../run-selection";

export interface RechenmauerWall {
  id: string;
  /** Reihenfolge: Basis zuerst, Spitze zuletzt. */
  rows: number[][];
}

export interface RechenmauerRun {
  seed: string;
  generatorVersion: 1 | 2;
  settings: RechenmauerRunSettings;
  walls: RechenmauerWall[];
}

export interface RechenmauerFieldEvent {
  seq: number;
  wallId: string;
  cellKey: string;
  value: number | null;
}

export interface RechenmauerSubmission {
  wallId: string;
  values: Record<string, number | null>;
  events: RechenmauerFieldEvent[];
  submitAttempts: number;
}

export interface RechenmauerCellAnalysis {
  cellKey: string;
  expected: number;
  entered: number | null;
  correct: boolean;
  errorKind: "none" | "omitted" | "primary" | "consistent_followup";
  plusMinusOne: boolean;
  inversion: boolean;
  inversionOpportunity: boolean;
  followupOpportunity: boolean;
  correctionAttempt: boolean;
  correctionCount: number;
}

export interface RechenmauerAnalysis {
  schemaVersion: "rechenmauer-analysis-v2.1";
  correct: number;
  total: number;
  primaryErrors: number;
  consistentFollowups: number;
  plusMinusOneErrors: number;
  inversions: number;
  submitAttempts: number;
  cells: RechenmauerCellAnalysis[];
  hypotheses: Array<{
    ruleId: "rechenmauer.consistent_followup" | "rechenmauer.plus_minus_one" | "rechenmauer.inversion";
    status: "triggered" | "not_triggered" | "insufficient_data";
    relevantCount: number;
    evidenceCount: number;
    correctionAttempt?: boolean;
    correctionCount?: number;
  }>;
}

function buildRows(base: number[]) {
  const rows = [base];
  while (rows.at(-1)!.length > 1) {
    const previous = rows.at(-1)!;
    rows.push(previous.slice(0, -1).map((value, index) => value + previous[index + 1]!));
  }
  return rows;
}

export function generateRechenmauerRun(config: RechenmauerExerciseConfig, seed: string, selection?: RunSelection): RechenmauerRun {
  const random = randomGenerator(seed);
  const count = selection?.count ?? config.count;
  const coefficientSum = 2 ** (config.height - 1);
  const baseMaximum = Math.max(2, Math.floor(config.maxResult / coefficientSum));
  const makeWall = () => {
    let rows: number[][];
    do {
      const base = Array.from({ length: config.height }, () => 1 + Math.floor(random() * baseMaximum));
      rows = buildRows(base);
    } while (rows.at(-1)![0]! > config.maxResult);
    return { id: "", rows };
  };
  const key = (wall: RechenmauerWall) => wall.rows[0]!.join("|");
  const theoreticalPoolSize = baseMaximum ** config.height;
  let selected: RechenmauerWall[];
  if (theoreticalPoolSize <= 10_000) {
    const candidates: RechenmauerWall[] = [];
    const collect = (base: number[]) => {
      if (base.length === config.height) {
        const rows = buildRows(base);
        if (rows.at(-1)![0]! <= config.maxResult) candidates.push({ id: "", rows });
        return;
      }
      for (let value = 1; value <= baseMaximum; value += 1) collect([...base, value]);
    };
    collect([]);
    selected = pickRunItems(candidates, count, random, key, selection?.unique);
  } else {
    selected = generateUniqueRunItems(count, makeWall, key, random, selection?.unique);
  }
  const walls = selected.map((wall, index) => ({ ...wall, id: `wall-${index + 1}` }));
  return {
    seed,
    generatorVersion: 2,
    settings: { count, height: config.height, maxResult: config.maxResult },
    walls,
  };
}

export function rechenmauerCellKey(rowIndex: number, cellIndex: number) {
  return `r${rowIndex}c${cellIndex}`;
}

function twoDigitInversion(expected: number) {
  if (expected < 10 || expected > 99 || expected % 10 === 0 || expected % 11 === 0) return null;
  return Number([...String(expected)].reverse().join(""));
}

export function analyzeRechenmauer(wall: RechenmauerWall, submission: RechenmauerSubmission): RechenmauerAnalysis {
  const eventCounts = new Map<string, number>();
  for (const event of submission.events) eventCounts.set(event.cellKey, (eventCounts.get(event.cellKey) ?? 0) + 1);

  const cells: RechenmauerCellAnalysis[] = [];
  for (let rowIndex = 1; rowIndex < wall.rows.length; rowIndex += 1) {
    const row = wall.rows[rowIndex]!;
    for (let cellIndex = 0; cellIndex < row.length; cellIndex += 1) {
      const cellKey = rechenmauerCellKey(rowIndex, cellIndex);
      const expected = row[cellIndex]!;
      const entered = submission.values[cellKey] ?? null;
      const correct = entered === expected;
      const belowLeftKey = rowIndex === 1 ? null : rechenmauerCellKey(rowIndex - 1, cellIndex);
      const belowRightKey = rowIndex === 1 ? null : rechenmauerCellKey(rowIndex - 1, cellIndex + 1);
      const belowLeft = rowIndex === 1 ? wall.rows[0]![cellIndex]! : submission.values[belowLeftKey!] ?? null;
      const belowRight = rowIndex === 1 ? wall.rows[0]![cellIndex + 1]! : submission.values[belowRightKey!] ?? null;
      const expectedBelowLeft = wall.rows[rowIndex - 1]![cellIndex]!;
      const expectedBelowRight = wall.rows[rowIndex - 1]![cellIndex + 1]!;
      const followupOpportunity = rowIndex >= 2 && entered !== null && belowLeft !== null && belowRight !== null
        && (belowLeft !== expectedBelowLeft || belowRight !== expectedBelowRight);
      const followsOwnValues = followupOpportunity && !correct && entered === belowLeft! + belowRight!;
      const inversionCandidate = twoDigitInversion(expected);
      cells.push({
        cellKey,
        expected,
        entered,
        correct,
        errorKind: correct ? "none" : entered === null ? "omitted" : followsOwnValues ? "consistent_followup" : "primary",
        plusMinusOne: entered !== null && Math.abs(entered - expected) === 1,
        inversion: inversionCandidate !== null && (entered === inversionCandidate || submission.events.some((event) => event.cellKey === cellKey && event.value === inversionCandidate)),
        inversionOpportunity: inversionCandidate !== null,
        followupOpportunity,
        correctionAttempt: inversionCandidate !== null && entered === expected && submission.events.some((event) => event.cellKey === cellKey && event.value === inversionCandidate),
        correctionCount: Math.max(0, (eventCounts.get(cellKey) ?? 0) - 1),
      });
    }
  }
  const count = (predicate: (cell: RechenmauerCellAnalysis) => boolean) => cells.filter(predicate).length;
  const hypothesis = (
    ruleId: RechenmauerAnalysis["hypotheses"][number]["ruleId"],
    relevantCount: number,
    evidenceCount: number,
  ): RechenmauerAnalysis["hypotheses"][number] => ({
    ruleId,
    relevantCount,
    evidenceCount,
    status: relevantCount === 0 ? "insufficient_data" : evidenceCount > 0 ? "triggered" : "not_triggered",
    ...(ruleId === "rechenmauer.inversion" ? {
      correctionAttempt: cells.some((cell) => cell.correctionAttempt),
      correctionCount: cells.filter((cell) => cell.correctionAttempt).reduce((sum, cell) => sum + cell.correctionCount, 0),
    } : {}),
  });
  const consistentFollowups = count((cell) => cell.errorKind === "consistent_followup");
  const plusMinusOneErrors = count((cell) => cell.errorKind === "primary" && cell.plusMinusOne);
  const inversions = count((cell) => cell.inversion);
  const followupOpportunities = count((cell) => cell.followupOpportunity);
  const inversionOpportunities = count((cell) => cell.inversionOpportunity);
  return {
    schemaVersion: "rechenmauer-analysis-v2.1",
    correct: count((cell) => cell.correct),
    total: cells.length,
    primaryErrors: count((cell) => cell.errorKind === "primary" || cell.errorKind === "omitted"),
    consistentFollowups,
    plusMinusOneErrors,
    inversions,
    submitAttempts: submission.submitAttempts,
    cells,
    hypotheses: [
      hypothesis("rechenmauer.consistent_followup", followupOpportunities, consistentFollowups),
      hypothesis("rechenmauer.plus_minus_one", cells.length, plusMinusOneErrors),
      hypothesis("rechenmauer.inversion", inversionOpportunities, inversions),
    ],
  };
}
